import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";

import ShowPasswordIcon from "../../assets/icons/svg/ShowPasswordIcon";
import HidePasswordIcon from "../../assets/icons/svg/HidePasswordIcon";
import localizationStrings from "../../statics/localization";
import LoginExperience from "./LoginExperience";
import { getLoginCopy } from "./loginCopy";
import { getLoginToken } from "../../api/login/loginApi";
import { SessionContext } from "../../context/SessionContext";
import Notification from "../notification";
import { getProfilesApi } from "../../api/user/user";
import useMountedRef from "../../utils/useMountedRef";
import { loginErrorMessage } from "./loginErrors";

const LoginPage = () => {
  const copy = getLoginCopy();
  const [isShownPassword, setIsShownPassword] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const history = useHistory();
  const loginPending = useRef(false);
  const mounted = useMountedRef();
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");
  const { setHasLoggedIn, setAllProfiles } = useContext(SessionContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm();

  const { emptyMessageText } = localizationStrings;

  const handleLogin = async (loginData) => {
    if (loginPending.current) return;
    loginPending.current = true;
    setLoggingIn(true);
    try {
      const { data } = await getLoginToken(
        loginData.userName,
        loginData.password
      );
      if (!mounted.current) return;
      if (!data?.access_token) throw new Error("Missing token");

      if (!!data && data?.access_token) {
        setHasLoggedIn(true);
        if (rememberMe) {
          localStorage.setItem("rememberedUsername", loginData.userName);
        } else {
          localStorage.removeItem("rememberedUsername");
        }
        localStorage.setItem("userToken", "Bearer " + data.access_token);
        // Store refresh token for logout
        if (data.refresh_token) {
          localStorage.setItem("refreshToken", data.refresh_token);
        }
        localStorage.setItem("hasLoggedIn", true);
        history.push("/selectprofile");
      }
    } catch (error) {
      if (mounted.current) {
        setLoginError(loginErrorMessage(error));
        setShowNotification(true);
      }
    } finally {
      loginPending.current = false;
      if (mounted.current) setLoggingIn(false);
    }
  };

  const handleTogglePassword = () => {
    setIsShownPassword((prevState) => !prevState);
  };

  const registerOptions = {
    userName: { required: emptyMessageText },
    password: { required: emptyMessageText }
  };

  const checkUserToken = async () => {
    const token = localStorage.getItem("userToken");
    if (token) {
      setShowLoading(true);
      try {
        const { data } = await getProfilesApi();
        if (!!data) {
          localStorage.setItem("profiles", JSON.stringify(data));
          setAllProfiles(data);
          setHasLoggedIn(true);
          setShowLoading(false);
          history.push("/selectprofile");
        }
      } catch (error) {
        console.warn("Auto login", error);
        setTimeout(() => {
          setShowLoading(false);
        }, 500);
      }
    }
  };

  useEffect(() => {
    setValue("password", "");
    setAllProfiles([]);
    localStorage.setItem("profiles", JSON.stringify([]));
    const savedUsername = localStorage.getItem("rememberedUsername");
    if (savedUsername) {
      checkUserToken();
      setValue("userName", savedUsername);
      setRememberMe(true);
    }
  }, []);

  return (
    <LoginExperience>
      {showLoading ? (
        <div className="signin-loading" role="status">
          <span className="signin-spinner" aria-hidden="true" />
          <span id="signin-form-title">{copy.session}</span>
        </div>
      ) : (
        <>
          <h1 id="signin-form-title">{copy.welcome}</h1>
          <form
            onSubmit={handleSubmit(handleLogin)}
            noValidate
            aria-labelledby="signin-form-title"
          >
            <div className="signin-field">
              <label htmlFor="signin-username">{copy.username}</label>
              <div className="signin-input">
                <input
                  id="signin-username"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder={copy.placeholder}
                  {...register("userName", registerOptions.userName)}
                  aria-invalid={!!errors.userName}
                  aria-describedby={
                    errors.userName ? "signin-username-error" : undefined
                  }
                  required
                />
              </div>
              {errors.userName && (
                <p
                  id="signin-username-error"
                  className="signin-error"
                  role="alert"
                >
                  {errors.userName.message}
                </p>
              )}
            </div>
            <div className="signin-field">
              <label htmlFor="signin-password">
                {localizationStrings.login.password}
              </label>
              <div className="signin-input">
                <input
                  id="signin-password"
                  type={isShownPassword ? "text" : "password"}
                  autoComplete="current-password"
                  {...register("password", registerOptions.password)}
                  aria-invalid={!!errors.password}
                  aria-describedby={
                    errors.password ? "signin-password-error" : undefined
                  }
                  required
                />
                <button
                  type="button"
                  className="signin-password-toggle"
                  onClick={handleTogglePassword}
                  aria-label={
                    isShownPassword ? copy.hidePassword : copy.showPassword
                  }
                  aria-pressed={isShownPassword}
                  aria-controls="signin-password"
                >
                  {isShownPassword ? (
                    <ShowPasswordIcon />
                  ) : (
                    <HidePasswordIcon />
                  )}
                </button>
              </div>
              {errors.password && (
                <p
                  id="signin-password-error"
                  className="signin-error"
                  role="alert"
                >
                  {errors.password.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="signin-submit"
              disabled={loggingIn}
              aria-busy={loggingIn}
            >
              {loggingIn ? copy.pending : localizationStrings.login.text}
              {loggingIn && (
                <span className="signin-spinner" aria-hidden="true" />
              )}
            </button>
            <div className="signin-options">
              <label className="signin-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                {localizationStrings.login.rememberMe}
              </label>
              <Link to="/forgot-password">
                {localizationStrings.login.forgotPassword}
              </Link>
            </div>
          </form>
          {showNotification && (
            <Notification
              text={loginError}
              confirmText={localizationStrings.close}
              onClickConfirm={() => setShowNotification(false)}
            />
          )}
        </>
      )}
    </LoginExperience>
  );
};

export default LoginPage;
